import moment, { Duration, Moment, MomentInput } from 'moment';
import VueI18n from 'vue-i18n';
import i18n from '@/general/i18n';
import { Nullish } from './typescript';

export enum FormatScheme {
  MOMENT, // https://momentjs.com/docs/#/parsing/string-format/
  DATE_FNS, // https://date-fns.org/v2.21.1/docs/parse
  DATE_PICKER, // https://vue3datepicker.com/props/formatting
}

export enum FormatCategory {
  DATE,
  TIME,
  DATE_TIME
}

export type DateTimeFormat = string;

function getMomentFormat(category: FormatCategory): DateTimeFormat {
  const localeData = moment.localeData();
  switch (category) {
    case FormatCategory.DATE:
      return localeData.longDateFormat('l');
    case FormatCategory.TIME:
      return localeData.longDateFormat('LT');
    case FormatCategory.DATE_TIME:
      return `${localeData.longDateFormat('l')} ${localeData.longDateFormat('lt')}`;
    default:
      throw new Error('Category not implemented');
  }
}

export function dateTimeFormat(category: FormatCategory, scheme: FormatScheme): DateTimeFormat {
  let format = getMomentFormat(category);
  if (scheme === FormatScheme.DATE_FNS) {
    format = format.replace(/Y/g, 'y');
    format = format.replace(/D/g, 'd');
  }
  if (scheme === FormatScheme.DATE_PICKER) {
    // TODO @Seppe kloppen deze search patterns? goeie case voor een unit test!
    format = format.replace(/M/, 'MM');
    format = format.replace(/D/, 'dd');
    format = format.replace('YYYY', 'yyyy');
    format = format.replace(' A', '');
    format = format.replace('h', 'HH');
  }
  return format;
}

/**
 * @deprecated not reactive!
 */
export function beforeToday(date: Date): boolean {
  return date < new Date(new Date().setHours(0, 0, 0, 0));
}

/**
 * @deprecated not reactive!
 */
export function beforeTodayEightOClock(date: Date): boolean {
  return date < new Date(new Date().setHours(8, 0, 0, 0));
}

/**
 * @deprecated not reactive!
 */
export function beforeNow(date: Date): boolean {
  return date < new Date();
}

export function afterDate(date1: Date, date2: Date): boolean {
  return date1 > date2;
}

// D-M-YYYY
export function momentDateFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.DATE, FormatScheme.MOMENT);
}

// HH:mm
export function momentTimeFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.TIME, FormatScheme.MOMENT);
}

// D-M-YYYY HH:mm
export function momentDateTimeFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.DATE_TIME, FormatScheme.MOMENT);
}

// D-M-yyyy
export function dateFnsDateFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.DATE, FormatScheme.DATE_FNS);
}

// HH:mm
export function dateFnsTimeFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.TIME, FormatScheme.DATE_FNS);
}

// d-M-yyyy HH:mm
export function dateFnsDateTimeFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.DATE_TIME, FormatScheme.DATE_FNS);
}

// dd-MM-yyyy
export function datepickerDateFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.DATE, FormatScheme.DATE_PICKER);
}

// H:m
export function datepickerTimeFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.TIME, FormatScheme.DATE_PICKER);
}

// dd-MM-yyyy H:m
export function datepickerDateTimeFormat(): DateTimeFormat {
  return dateTimeFormat(FormatCategory.DATE_TIME, FormatScheme.DATE_PICKER);
}

/**
 * @deprecated not reactive! Use TimeService helper functions in stead.
 */
export function yesterday(): Moment {
  return moment().subtract(1, 'd');
}

/**
 * @deprecated not reactive! Use TimeService helper functions in stead.
 */
export function today(): Moment {
  return moment();
}

/**
 * @deprecated not reactive! Use TimeService helper functions in stead.
 */
export function tomorrow(): Moment {
  return moment().add(1, 'd');
}

/**
 * @deprecated not reactive!
 */
export function getDayLabel(date: Moment): VueI18n.TranslateResult {
  if (!date) return '';
  if (date.isSame(yesterday(), 'day')) return i18n.global.t('general.yesterday');
  if (date.isSame(today(), 'day')) return i18n.global.t('general.today');
  if (date.isSame(tomorrow(), 'day')) return i18n.global.t('general.tomorrow');
  return date.format('ll');
}

/**
 * @deprecated not reactive! Use TimeService helper functions in stead.
 */
export function isYesterday(date: Moment): boolean {
  return date.isSame(yesterday(), 'day');
}

/**
 * @deprecated not reactive! Use TimeService helper functions in stead.
 */
export function isToday(date: Moment): boolean {
  return date.isSame(today(), 'day');
}

/**
 * @deprecated not reactive!
 */
export function isSameOrAfterToday(date: Moment): boolean {
  return date.isSameOrAfter(today(), 'day');
}

/**
 * @deprecated not reactive! Use TimeService helper functions in stead.
 */
export function isTomorrow(date: Moment): boolean {
  return date.isSame(tomorrow(), 'day');
}

export function formatDuration(duration: Duration): string {
  return moment.utc(duration.as('milliseconds')).format(momentTimeFormat());
}

export function durationAsMinutes(duration: string): number {
  const [hours, minutes] = duration.split(':').map(Number);
  return hours * 60 + minutes;
}

export function minutesSinceMidnight(m: Moment): number {
  return m.hours() * 60 + m.minutes();
}

export function isAtMidnight(m: Moment): boolean {
  return m.isSame(m.clone().startOf('day'));
}

export function formatHours(hours: number, format: string): string {
  return moment().startOf('day').add(hours, 'hours').format(format);
}

export function formatWithMoment(value: MomentInput, formatter: (m: Moment) => string): string {
  if (!value) return '';
  const m = moment(value);
  if (!m.isValid()) return '';
  return formatter(m);
}

export function formatDate(value: MomentInput): string {
  return formatWithMoment(value, m => m.format('LL'));
}

export function formatShortDate(value: MomentInput): string {
  return formatWithMoment(value, m => m.format('D/M/YYYY'));
}

export function formatTime(value: MomentInput): string {
  return formatWithMoment(value, m => m.format(momentTimeFormat()));
}

export function formatDateTime(value: MomentInput): string {
  return formatWithMoment(value, m => m.format('LLLL'));
}

export function formatShortDateTime(value: MomentInput): string {
  return formatWithMoment(value, m => m.format('DD/MM/YYYY HH:mm'));
}

export function formatDownloadDateTime(value: MomentInput): string {
  return formatWithMoment(value, m => m.format('DD_MM_YYYY_HH:mm'));
}

export function formatShortRelativeDateTime(value: MomentInput): string {
  return formatWithMoment(value, m => {
    return m.calendar(null, {
      sameDay: `[${i18n.global.t('general.today')}] HH:mm`,
      nextDay: `[${i18n.global.t('general.tomorrow')}] HH:mm`,
      nextWeek: 'DD/MM/YYYY HH:mm',
      lastDay: `[${i18n.global.t('general.yesterday')}] HH:mm`,
      lastWeek: 'DD/MM/YYYY HH:mm',
      sameElse: 'DD/MM/YYYY HH:mm',
    });
  });
}

export function formatRelativeDateTime(value: MomentInput, now = moment()): string {
  return formatWithMoment(value, m => {
    if (m.isSame(now, 'day')) {
      return m.format('LT');
    }

    const diffInDays = m.clone().startOf('day').diff(now.clone().startOf('day'), 'days');

    if (diffInDays === -1 || diffInDays === 1) {
      return m.calendar();
    }

    return m.format('LLL');
  });
}

export function formatRelativeDate(value: MomentInput): string {
  return formatWithMoment(value, m => {
    return m.calendar(null, {
      sameDay: `[${i18n.global.t('general.today')}]`,
      nextDay: `[${i18n.global.t('general.tomorrow')}]`,
      nextWeek: 'DD/MM/YYYY',
      lastDay: `[${i18n.global.t('general.yesterday')}]`,
      lastWeek: 'DD/MM/YYYY',
      sameElse: 'DD/MM/YYYY',
    });
  });
}

export function durationFromMinutes(value: any): string {
  if (typeof value !== 'number') return '';
  const sanitized = Math.floor(Math.abs(value));
  const sign = value > -1 ? '' : '-';
  const hoursPart = Math.floor(sanitized / 60);
  const minutesPart = (sanitized % 60).toString().padStart(2, '0');
  return `${sign}${hoursPart}:${minutesPart}`;
}

export function getWaitingMinutes(start: Nullish<moment.Moment>, end: Nullish<moment.Moment>, limit = 6060): number | undefined {
  if (start && end) {
    const minutes = end.diff(start, 'minutes');
    if (minutes < limit) {
      return minutes;
    }
  }
}